import { createProductSearchPrefetcher } from '../../utils/createProductSearchPrefetcher';
import loadColorVariation from '../../utils/loadColorVariation';
import { urlWithSearchParams } from '../../../common/utils/url';
import getFrontConfig from '../../../common/getFrontConfig';

const { searchAI, urls: { searchImage } } = getFrontConfig();
const storageSearchHistoryKey = 'searchHistory';

const ArrayUtils = {
  getLastElement: (arr) => arr[arr.length - 1],
  moveElementToLastPosition: (arr, index) => arr.push(arr.splice(index, 1)[0]),
  removeFirstElement: (arr) => arr.shift(),
};

const showAlertError = (error) => new Promise(() => {
  if (error || searchAI.resources?.fileError) {
    // eslint-disable-next-line no-alert
    alert(error || searchAI.resources?.fileError);
  }
});

function isValidFile(file) {
  return file
    && file.size < 5.0e+6
    && ['image/jpg',
      'image/jpeg',
      'image/png',
    ].includes(file.type);
}

const base64ImageReader = (file) => new Promise((resolve, reject) => {
  if (isValidFile(file)) {
    const reader = new FileReader();
    // eslint-disable-next-line listeners/no-missing-remove-event-listener, listeners/no-inline-function-event-listener
    reader.addEventListener('load', () => {
      if (reader.result) {
        resolve(reader.result);
      } else {
        reject();
      }
    }, true);
    reader.readAsDataURL(file);
  } else {
    reject();
  }
});

function createSearchResult(query, { productSearch, suggestedTerms }) {
  const suggestedMatches = suggestedTerms?.suggestedMatches
    ? suggestedTerms.suggestedMatches
    : [];
  const match = suggestedMatches.length > 0 ? suggestedMatches[0] : query;

  return {
    query,
    match,
    products: productSearch.products,
    prefetcher: createProductSearchPrefetcher(productSearch),
    productSearch,
    suggestedTerms,
    extend(newSuggestedTerms) {
      return {
        ...this,
        suggestedTerms: newSuggestedTerms,
      };
    },
    getQuery() {
      return this.query;
    },
    getSuggestedMatch() {
      return this.match;
    },
    getSuggestedTerms() {
      return this.suggestedTerms;
    },
    pushProducts(newProductSearch) {
      this.products = [...this.products, ...newProductSearch.products];
      this.productSearch = { ...newProductSearch };
    },
    toState() {
      return {
        match: this.match,
        products: this.products,
        productSearch: this.productSearch,
        suggestedTerms: this.suggestedTerms,
      };
    },
  };
}

const searchCache = (function createSearchCache() {
  const matchResultCache = {};
  const matchResultCacheHistory = JSON.parse(localStorage.getItem(storageSearchHistoryKey)) || [];
  const matchResultCacheMaxLength = 5;
  const queryMatchCache = {};
  const querySuggestedTermsCache = {};

  const safeKey = (key) => key.toLowerCase();
  const updateMatchResultCacheHistory = (match) => {
    if (matchResultCacheHistory.includes(match)) {
      ArrayUtils.moveElementToLastPosition(matchResultCacheHistory, match);
    } else {
      matchResultCacheHistory.push(match);
    }
    localStorage.setItem(storageSearchHistoryKey, JSON.stringify(matchResultCacheHistory));
  };

  return {
    get(query) {
      const safeQueryKey = safeKey(query);
      const suggestedTerms = querySuggestedTermsCache[safeQueryKey];
      const matchKey = queryMatchCache[safeQueryKey];
      const result = matchKey && matchResultCache[matchKey];
      result?.extend(suggestedTerms);
      return result;
    },
    getLastQuery() {
      return matchResultCacheHistory.pop() || '';
    },
    has(query) {
      const safeQueryKey = safeKey(query);
      const match = queryMatchCache[safeQueryKey];
      return Object.prototype.hasOwnProperty.call(matchResultCache, [match]);
    },
    set(query, searchResult) {
      const suggestedMatch = searchResult.getSuggestedMatch();

      const safeQueryKey = safeKey(query);
      const safeMatchKey = suggestedMatch
        ? safeKey(suggestedMatch)
        : safeQueryKey;

      queryMatchCache[safeQueryKey] = safeMatchKey;
      matchResultCache[safeMatchKey] = searchResult;

      querySuggestedTermsCache[safeQueryKey] = searchResult.getSuggestedTerms();

      updateMatchResultCacheHistory(safeMatchKey);

      if (matchResultCacheHistory.length > matchResultCacheMaxLength) {
        const removedMatch = ArrayUtils.removeFirstElement(
          matchResultCacheHistory,
        );
        delete matchResultCache[removedMatch];
      }

      return this;
    },
    push(searchResult) {
      const query = searchResult.getQuery();
      if (!this.has(query)) {
        this.set(query, searchResult);
      }
      return this;
    },
    replace(searchResult) {
      const query = searchResult.getQuery();
      this.set(query, searchResult);
      return this;
    },
    getRecent() {
      return this.get(matchResultCacheHistory);
    },
  };
}());

/**
 * @typedef {Object} SearchState
 * @property {string|null} query - The current search query.
 * @property {string|null} baseUrl - The base URL for search requests.
 * @property {string|null} base64Image - Base64-encoded image for visual search.
 * @property {boolean} isCached - Indicates if the search results are cached.
 * @property {boolean} isModalOpen - Indicates if a modal is open.
 * @property {boolean} isLoading - Indicates if a search request is in progress.
 * @property {number} numSearches - The number of searches performed.
 * @property {Object} results - The current search results.
 * @property {Object|null|undefined} results.match - The matched search result.
 * @property {Array|null} results.products - List of found products.
 * @property {Object|null} results.productSearch - Product search metadata.
 * @property {Array<string>} results.suggestedTerms - Suggested search terms.
 * @property {Object} defaultResults - The default search results.
 * @property {Array|null} defaultResults.products - Default product results.
 * @property {Object|null} defaultResults.productSearch - Default product search metadata.
 * @property {Array<string>} defaultResults.suggestedTerms - Default suggested terms.
 */

/** @type {SearchState} */
const initialState = {
  query: null,
  baseUrl: null,
  base64Image: null,
  currentSearchAbortController: null,
  isCached: false,
  isModalOpen: false,
  isLoading: false,
  numSearches: 0,
  results: {
    match: undefined,
    products: null,
    productSearch: null,
    suggestedTerms: [],
  },
  defaultResults: {
    products: null,
    productSearch: null,
    suggestedTerms: [],
  },
};

export const types = {
  updateResult: 'search/updateResult',
  updateValue: 'search/updateValue',
  setDefaultResults: 'search/setDefaultResults',
  search: 'search/search',
  searchImage: 'search/searchImage',
  readBase64Image: 'search/readBase64Image',
  setBase64Image: 'search/setBase64Image',
  searchNextPage: 'search/searchNextPage',
  setBaseUrl: 'search/setBaseUrl',
  setOpenModal: 'search/setOpenModal',
  loadColorVariation: 'search/loadColorVariation',
};

export const getters = {
  areFirstSearchResults: 'search/areFirstSearchResults',
  lastQuery: 'search/lastQuery',
};

export default {
  namespaced: true,
  state: initialState,
  getters: {
    areFirstSearchResults: (state) => state.numSearches === 1,
    lastQuery: (state) => state.query || searchCache.getLastQuery(),
  },
  mutations: {
    setBaseUrl(state, baseUrl) {
      state.baseUrl = baseUrl;
    },
    setIsCached(state, isCached) {
      state.isCached = isCached;
    },
    setBase64Image(state, base64Image) {
      state.base64Image = base64Image;
      if (!base64Image && state.query === '') {
        state.results = state.defaultResults;
      }
    },
    setOpenModal(state, isModalOpen) {
      state.isModalOpen = isModalOpen;
      if (!isModalOpen) {
        state.base64Image = null;
        state.results = state.defaultResults;
      }
    },
    setIsLoading(state, isLoading) {
      state.isLoading = isLoading;
    },
    /**
     * Increment the search counter in one.
     * @param {SearchState} state current state
     */
    updateNumSearches(state) {
      state.numSearches += 1;
    },
    updateResult(state, searchResult) {
      state.results = searchResult.toState();
    },
    setDefaultResults(state) {
      state.results = state.defaultResults;
    },
    updateValue(state, value = '') {
      state.query = value;
      if (value === '') {
        state.results = state.defaultResults;
      }
    },
    setCurrentSearchAbortController(state, controller) {
      state.currentSearchAbortController = controller;
    },
  },
  actions: {
    readBase64Image({ commit, dispatch }, target) {
      const file = target?.files[0];
      if (!file) return;
      commit('setIsLoading', true);
      base64ImageReader(file)
        .then((base64Image) => {
          commit('updateValue', '');
          commit('setBase64Image', base64Image);
          commit('setOpenModal', true);
          dispatch('searchImage', { start: 0 });
        })
        .catch(() => {
          commit('updateValue', '');
          commit('setBase64Image', null);
          commit('setIsLoading', false);
          showAlertError();
        });
    },
    searchImage({ commit, state }) {
      const { base64Image, start = 0 } = state;
      if (base64Image && base64Image.length > 0) {
        commit('setIsLoading', true);
        fetch(searchImage, {
          method: 'post',
          body: JSON.stringify({
            base64_image: base64Image.replace(/^data:image\/[a-z]+;base64,/, ''),
            start,
          }),
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.c_alert && data.c_message) {
              throw new Error(data.c_message);
            }
            const searchResult = createSearchResult('imageSearch', data);
            commit('updateResult', searchResult);
            commit('updateNumSearches');
            commit('setIsCached', false);
          })
          .catch((data) => {
            commit('setBase64Image', null);
            showAlertError(data.c_message);
          })
          .finally(() => commit('setIsLoading', false));
      }
    },
    search({ commit, state }) {
      const { query } = state;
      if (state.results?.productSearch?.query === query) {
        return;
      }
      if (state.currentSearchAbortController) {
        state.currentSearchAbortController.abort();
      }
      const abortController = new AbortController();
      commit('setCurrentSearchAbortController', abortController);
      commit('setBase64Image', null);
      commit('setIsLoading', true);
      commit('setDefaultResults');

      if (searchCache.has(query)) {
        commit('updateResult', searchCache.get(query));
        commit('setIsCached', true);
        commit('setIsLoading', false);
        return;
      }

      const searchParams = { format: 'json', q: query, platform: 'web' };
      if (searchAI.isFrontTestEnabled) {
        searchParams.isFrontTestEnabled = true;
      }
      const baseUrl = urlWithSearchParams(state.baseUrl, searchParams);
      fetch(baseUrl, {
        method: 'GET',
        signal: abortController.signal,
      }).then((response) => response.json())
        .then((data) => {
          if (data.redirectUrl) {
            window.location.href = data.redirectUrl;
          } else {
            const searchResult = createSearchResult(query, data);
            commit('updateResult', searchResult);
            commit('updateNumSearches');
            commit('setIsCached', false);
            searchCache.push(searchResult);
          }
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            commit('setDefaultResults');
          }
        })
        .finally(() => {
          if (abortController === state.currentSearchAbortController) {
            commit('setIsLoading', false);
          }
        });
    },
    searchNextPage({ commit, state }, url) {
      const { query } = state;
      const searchResult = searchCache.get(query);
      const searchParams = { format: 'json', platform: 'web' };
      if (searchAI.isFrontTestEnabled) {
        searchParams.isFrontTestEnabled = true;
      }
      const baseUrl = urlWithSearchParams(url, searchParams);
      if (searchResult && searchResult.prefetcher) {
        searchResult.prefetcher.get(baseUrl).then((productSearch) => {
          searchResult.pushProducts(productSearch);
          commit('updateResult', searchResult);
          searchCache.replace(searchResult);
        });
      }
    },
    loadColorVariation({ state }, {
      color, type, index,
    }) {
      loadColorVariation({ state }, {
        color, type, index,
      });
    },
  },
};
